/**
 * AI Agent — страница работающего ReAct-агента для отладки/проектирования ClickHouse.
 *
 * Отличается от обычного AI-Tutor чата:
 *   - Видны "шаги" агента: tool_call → tool_result → next step
 *   - Финальный ответ виден отдельно (markdown)
 *   - Минимальный sidebar (без истории сессий пока)
 */
import { useCallback, useRef, useState } from "react";
import { API_HOST } from "@/constants";
import AgentTrace, { AgentEvent } from "@/components/AgentTrace";
import AgentMessageContent from "@/components/AgentMessageContent";

interface AgentTurn {
  question: string;
  events: AgentEvent[];
  finalAnswer: string;
  isRunning: boolean;
  finalReason?: string;
  stepsUsed?: number;
  tokensUsed?: number;
  elapsedMs?: number;
  error?: string;
}

type AgentRole = "debug" | "schema_design";

interface AgentSession {
  sessionId?: string;
  role: AgentRole;
  turns: AgentTurn[];
}

const PRESET_BY_ROLE: Record<AgentRole, string[]> = {
  debug: [
    "Какие таблицы есть в базе demo?",
    "Объясни, как работает ReplacingMergeTree FINAL",
    "Запрос SELECT * FROM events WHERE date > now() - INTERVAL 7 DAY работает 30 секунд. Как ускорить?",
    "Получаю DB::Exception: Memory limit (for query) exceeded. Что делать?",
  ],
  schema_design: [
    "Спроектируй таблицу для лога событий: ~1 млрд строк/день, поля timestamp, user_id, action, retention 90 дней",
    "Нужна агрегирующая витрина: dau/wau/mau по user_id, обновляется в реальном времени",
    "Спроектируй таблицу для хранения векторных эмбеддингов с быстрым ANN-поиском",
    "Спроектируй timeseries таблицу для метрик с TTL 30 дней + downsampling на 1 год",
  ],
};

export default function AgentPage() {
  const [input, setInput] = useState<string>("");
  const [session, setSession] = useState<AgentSession>({
    sessionId: undefined,
    role: "debug",
    turns: [],
  });
  const abortRef = useRef<AbortController | null>(null);

  const isRunning = session.turns.some((t) => t.isRunning);

  const stop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const resetSession = useCallback(() => {
    stop();
    setSession({ sessionId: undefined, role: session.role, turns: [] });
  }, [stop, session.role]);

  const submit = useCallback(async (question: string) => {
    if (!question.trim() || isRunning) return;

    stop();
    const controller = new AbortController();
    abortRef.current = controller;

    // Append turn
    const newTurn: AgentTurn = { question, events: [], finalAnswer: "", isRunning: true };
    setSession((prev) => ({ ...prev, turns: [...prev.turns, newTurn] }));
    setInput("");

    const turnIndex = session.turns.length;

    try {
      const response = await fetch(`${API_HOST}/api/ai/agent/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: question,
          session_id: session.sessionId,
          role: session.role,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: response.statusText }));
        const detail = err?.detail || response.statusText;
        // Friendly handling 429: показываем сколько ждать
        let displayError = String(detail);
        if (response.status === 429) {
          const retryAfter = response.headers.get("retry-after");
          const retrySec = retryAfter ? Number(retryAfter) : null;
          if (retrySec && !Number.isNaN(retrySec)) {
            const mins = Math.ceil(retrySec / 60);
            displayError = `⏳ Слишком много запросов. Подожди ~${mins > 1 ? mins + " мин" : retrySec + " сек"} и попробуй снова.`;
          } else {
            displayError = `⏳ Слишком много запросов. Подожди немного и попробуй снова.`;
          }
        }
        setSession((prev) => {
          const turns = [...prev.turns];
          if (turns[turnIndex]) turns[turnIndex] = { ...turns[turnIndex], isRunning: false, error: displayError };
          return { ...prev, turns };
        });
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body reader");
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const evt = JSON.parse(line.slice(6)) as AgentEvent;
            setSession((prev) => {
              const turns = [...prev.turns];
              const t = turns[turnIndex];
              if (!t) return prev;
              const updated: AgentTurn = { ...t, events: [...t.events, evt] };
              if (evt.type === "answer_chunk" && typeof evt.content === "string") {
                updated.finalAnswer = (t.finalAnswer || "") + evt.content;
              }
              if (evt.type === "done") {
                updated.isRunning = false;
                updated.finalReason = evt.final_reason;
                updated.stepsUsed = evt.steps_used;
                updated.tokensUsed = evt.tokens_used;
                updated.elapsedMs = evt.elapsed_ms;
                if (evt.final_answer && !updated.finalAnswer) updated.finalAnswer = evt.final_answer;
              }
              if (evt.type === "error") {
                updated.isRunning = false;
                updated.error = evt.error || "Unknown error";
              }
              turns[turnIndex] = updated;

              // Сохраняем session_id из start event
              const newSessionId = evt.type === "start" && evt.session_id ? evt.session_id : prev.sessionId;
              return { ...prev, sessionId: newSessionId, turns };
            });
          } catch {
            // ignore malformed line
          }
        }
      }
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setSession((prev) => {
          const turns = [...prev.turns];
          if (turns[turnIndex]) turns[turnIndex] = { ...turns[turnIndex], isRunning: false, error: String(e?.message || e) };
          return { ...prev, turns };
        });
      }
    } finally {
      abortRef.current = null;
      setSession((prev) => {
        const turns = [...prev.turns];
        if (turns[turnIndex]) turns[turnIndex] = { ...turns[turnIndex], isRunning: false };
        return { ...prev, turns };
      });
    }
  }, [isRunning, session.sessionId, session.role, session.turns.length, stop]);

  const presets = PRESET_BY_ROLE[session.role];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="mx-auto max-w-4xl p-6">
        <header className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">AI Agent — ClickHouse</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {session.role === "schema_design"
                ? "Multi-turn проектирование схем таблиц. Уточняет требования, предлагает варианты, тестирует на demo."
                : "Решает сложные задачи: отладка медленных запросов, объяснения, диагностика ошибок."}
            </p>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <select
              value={session.role}
              onChange={(e) => {
                const newRole = e.target.value as AgentRole;
                setSession({ sessionId: undefined, role: newRole, turns: [] });
              }}
              disabled={isRunning}
              className="text-sm rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1"
            >
              <option value="debug">Debug / explanation</option>
              <option value="schema_design">Schema design</option>
            </select>
            {session.turns.length > 0 && (
              <button
                onClick={resetSession}
                disabled={isRunning}
                className="text-xs px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Новая сессия
              </button>
            )}
          </div>
        </header>

        {/* Turns history */}
        <div className="space-y-4 mb-4">
          {session.turns.map((turn, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-xs text-gray-500 mb-1">Turn {i + 1}</div>
              <div className="text-sm mb-3 p-2 bg-gray-100 dark:bg-gray-700/50 rounded">
                <strong className="text-gray-700 dark:text-gray-300">Вы: </strong>
                {turn.question}
              </div>

              <AgentTrace events={turn.events} isRunning={turn.isRunning} />

              {turn.finalAnswer && (
                <div className="mt-4">
                  <div className="text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Ответ:</div>
                  <div className="prose prose-sm dark:prose-invert max-w-none p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                    <AgentMessageContent content={turn.finalAnswer} />
                  </div>
                </div>
              )}

              {turn.error && (
                <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded text-sm">
                  ⚠️ Ошибка: {turn.error}
                </div>
              )}

              {!turn.isRunning && turn.finalReason && (
                <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-3">
                  <span>{turn.finalReason}</span>
                  {turn.stepsUsed != null && <span>{turn.stepsUsed} шагов</span>}
                  {turn.tokensUsed != null && <span>{turn.tokensUsed} tokens</span>}
                  {turn.elapsedMs != null && <span>{(turn.elapsedMs / 1000).toFixed(1)}s</span>}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Input */}
        <div className="sticky bottom-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-lg">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submit(input);
              }
            }}
            placeholder={
              session.turns.length === 0
                ? (session.role === "schema_design"
                    ? "Опиши требования: объём данных, retention, типичные запросы…"
                    : "Опиши задачу: что не работает, какую ошибку видишь…")
                : "Уточни, продолжи, попроси изменить…"
            }
            className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-2 min-h-[80px] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isRunning}
          />
          <div className="flex items-center justify-between mt-2 gap-2">
            <div className="flex flex-wrap gap-1 flex-1">
              {session.turns.length === 0 && presets.map((q) => (
                <button
                  key={q}
                  onClick={() => setInput(q)}
                  className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                  disabled={isRunning}
                >
                  {q.slice(0, 35)}{q.length > 35 ? "…" : ""}
                </button>
              ))}
            </div>
            <div className="flex gap-2 shrink-0">
              {isRunning && (
                <button
                  onClick={stop}
                  className="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-sm"
                >
                  Стоп
                </button>
              )}
              <button
                onClick={() => submit(input)}
                disabled={!input.trim() || isRunning}
                className="px-4 py-1 rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm"
              >
                {isRunning ? "Думаю…" : "Отправить"}
              </button>
            </div>
          </div>
          <div className="text-[10px] text-gray-500 mt-1">⌘/Ctrl+Enter для отправки</div>
        </div>
      </div>
    </div>
  );
}
